import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const SERVICE_LINKS = [
  { href: "/samoan-church-uniforms", label: "Samoan Church Uniforms" },
  { href: "/tongan-church-uniforms", label: "Tongan Church Uniforms" },
  { href: "/cook-islands-uniforms", label: "Cook Islands Uniforms" },
  { href: "/church-conference-uniforms", label: "Conference Uniforms" },
  { href: "/reunion-polo-shirts", label: "Reunion Polo Shirts" },
  { href: "/church-uniforms-auckland", label: "Church Uniforms Auckland" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-zinc-950/85 backdrop-blur-lg border-b border-zinc-900"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <div className="w-9 h-9 bg-teal-400 grid place-items-center shrink-0">
            <span className="font-display font-bold text-zinc-950 text-lg leading-none">T</span>
          </div>
          <div className="leading-tight">
            <div className="font-display font-semibold tracking-tight uppercase text-base md:text-lg">
              Tribal <span className="text-teal-400">Designz</span>
            </div>
            <div className="hidden sm:block text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">
              Pacific Heritage Uniforms
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-services-trigger"
              className="text-sm font-semibold tracking-wide uppercase text-zinc-300 hover:text-teal-400 transition-colors inline-flex items-center gap-1"
            >
              Collections <ChevronDown size={14} />
            </button>
            {servicesOpen && (
              <div className="absolute top-full left-0 pt-3" data-testid="nav-services-menu">
                <div className="w-72 bg-[#060d24] border border-zinc-800 py-2">
                  {SERVICE_LINKS.map((s) => (
                    <Link
                      key={s.href}
                      to={s.href}
                      className="block px-5 py-3 text-sm text-zinc-300 hover:bg-zinc-900 hover:text-teal-400 uppercase tracking-wider"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a href="/#pricing" data-testid="nav-pricing" className="text-sm font-semibold tracking-wide uppercase text-zinc-300 hover:text-teal-400">Pricing</a>
          <a href="/#how-it-works" data-testid="nav-how" className="text-sm font-semibold tracking-wide uppercase text-zinc-300 hover:text-teal-400">How It Works</a>
          <a href="/#gallery" data-testid="nav-gallery" className="text-sm font-semibold tracking-wide uppercase text-zinc-300 hover:text-teal-400">Gallery</a>
          <Link to="/blog" data-testid="nav-blog" className="text-sm font-semibold tracking-wide uppercase text-zinc-300 hover:text-teal-400">Blog</Link>
          <a href="/#contact" data-testid="nav-contact" className="text-sm font-semibold tracking-wide uppercase text-zinc-300 hover:text-teal-400">Contact</a>
          <a
            href="/#contact"
            data-testid="nav-cta-quote"
            className="btn-primary text-sm"
          >
            Get Free Mockup
          </a>
        </nav>

        <div className="lg:hidden flex items-center gap-2">
          <a
            href="/#contact"
            data-testid="mobile-header-quote"
            className="bg-teal-400 text-zinc-950 font-bold uppercase tracking-wider text-[11px] px-3 py-2 hover:bg-teal-300 transition-colors"
          >
            Get Free Quote
          </a>
          <button
            className="text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-toggle-mobile"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-zinc-950 border-t border-zinc-900 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-3">Collections</div>
              <div className="flex flex-col gap-3 pl-1 border-l border-zinc-800">
                {SERVICE_LINKS.map((s) => (
                  <Link
                    key={s.href}
                    to={s.href}
                    onClick={() => setOpen(false)}
                    className="text-sm font-semibold uppercase tracking-wide text-zinc-200 hover:text-teal-400 pl-3"
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-4 pt-4 border-t border-zinc-800">
              <a href="/#pricing" onClick={() => setOpen(false)} className="text-base font-semibold uppercase tracking-wider text-zinc-200 hover:text-teal-400">Pricing</a>
              <a href="/#how-it-works" onClick={() => setOpen(false)} className="text-base font-semibold uppercase tracking-wider text-zinc-200 hover:text-teal-400">How It Works</a>
              <a href="/#gallery" onClick={() => setOpen(false)} className="text-base font-semibold uppercase tracking-wider text-zinc-200 hover:text-teal-400">Gallery</a>
              <Link to="/blog" onClick={() => setOpen(false)} className="text-base font-semibold uppercase tracking-wider text-zinc-200 hover:text-teal-400">Blog</Link>
              <a href="/#contact" onClick={() => setOpen(false)} className="text-base font-semibold uppercase tracking-wider text-zinc-200 hover:text-teal-400">Contact</a>
            </div>
            <a
              href="/#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-cta-quote"
              className="btn-primary text-center mt-2"
            >
              Get Free Mockup
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
